/**
 * TranscriptionTextTranslated.tsx
 *
 * Regular translated transcript screen. Parallel to `TranscriptionText`
 * (the original-language regular view). Renders translation phrases at
 * their lead positions, anchored to paragraph groups derived from
 * `transcriptElements`. Cut/removed source elements are filtered out
 * before grouping (use `TranscriptionDiffTranslated` for the redline
 * variant that keeps them visible).
 *
 * Data shaping is shared with `TranscriptionDiffTranslated` via
 * `useTranslatedRender`; this component only owns the JSX and
 * regular-mode CSS classes.
 */

import { useSpeakerLabels } from '@src/hooks/useSpeakerLabels';
import { resolveTranslatedSelectionPhrases } from '@src/stores-v2/resolveTranslatedSelectionPhrases';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { LogManager } from '@src/utility/Logger';
import { groupElementsByParagraph } from '@src/utility/transcriptGrouping';
import { overlayHighlightStyles } from '@src/utility/transcriptQuoteUtils';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

import {
  CHANGED_PHRASE_TINT,
  changedPhraseHighlightCss,
  resolveChangedWordTokens,
} from './changedPhraseKit';
import { usePhraseEditing } from './hooks/usePhraseEditing';
import { disableForUnmappedSelection } from './hooks/useTranslatedContextMenuActions';
import {
  type TranslatedSortedPhrase,
  useTranslatedRender,
} from './hooks/useTranslatedRender';
import { InlineSpanReplaceLayer } from './InlineSpanReplaceLayer';
import {
  ChapterHeadingRow,
  SectionHeadingRow,
  StructureGapRow,
} from './reelStructure/ReelStructureHeadings';
import { buildReelTranscriptRows } from './reelStructure/reelTranscriptRows';
import { useReelTranscriptStructure } from './reelStructure/ReelTranscriptStructureContext';
import { revealCollapsedWord } from './reelStructure/revealCollapsedWord';
import { buildTranslatedReelGroups } from './reelStructure/translatedReelGroups';
import {
  SpeakerHeader,
  SpeakerLabel,
  SpeakerTime,
} from './speakerHeaderStyles';
import {
  CURSOR_ALLOWANCE_PX,
  topicRailCss,
  topicRailProps,
} from './topicRailStyles';
import { TranscriptContextMenu } from './TranscriptContextMenu';
import { renderPhrasesWithUnderline } from './translatedOverlayUnderline';
import { TranslatedPhraseContextMenu } from './TranslatedPhraseContextMenu';
import {
  isEdited,
  mapCoveredSourceToElementRange,
  resolveSourceText,
} from './translatedPhraseEditing';
import { TranslatedPhraseEditorPopover } from './TranslatedPhraseEditorPopover';
import { isPhraseInTranslatedSelection } from './translatedSelectionGuard';

import type {
  ReviewHandoffProp,
  ReviewHandoffRequest,
} from '@src/components/aiVoices/reviewHandoff';
import type {
  PendingSpan,
  ReDubSelectionRequest,
} from './hooks/useSpanReplace';
import type { SpeakerClickInfo } from './TranscriptionText';
import type {
  TranscriptContextMenuActions,
  TranscriptContextMenuDisabledReasons,
} from '@src/types.ts/general';
import type { TranscriptElement } from '@src/types.ts/transcript';
import type { TranslationPhrase } from '@src/types.ts/translation';
import type { HighlightRange } from '@src/utility/transcriptQuoteUtils';

interface TranscriptionTextTranslatedProps {
  language: string;
  transcriptElements: TranscriptElement[] | undefined;
  currentTime?: number;
  isPlaying?: boolean;
  /** Enables right-click "Edit translation" menu. Default true. */
  editable?: boolean;
  onWordClick?: (initialIndex: number) => void;
  /**
   * PLAT-11607 — clicking a speaker header opens the speaker context menu
   * (unassign / reassign everywhere / reassign this section), exactly as in
   * the original-language view. Absent → the label is inert, as before.
   */
  onSpeakerClick?: (info: SpeakerClickInfo) => void;
  /**
   * When set, only paragraphs whose paragraph_id is in this set render.
   * Speaker headers / timestamp rows for filtered paragraphs are dropped
   * along with them.
   */
  visibleParagraphIds?: ReadonlySet<string>;
  /**
   * When set, paragraphs whose paragraph_id is in this set get the topic
   * rail in the left gutter (does not filter the render).
   */
  highlightedParagraphIds?: ReadonlySet<string>;
  /**
   * Per-paragraph rail colors keyed by paragraph_id. Falls back to the
   * default blue when missing.
   */
  paragraphHighlightColors?: ReadonlyMap<string, string>;
  /**
   * Paragraph_id of the currently-active topic match — gets a solid rail
   * while the other matches stay muted. Optional.
   */
  activeParagraphId?: string;
  /**
   * Overlay ranges in phrase-lead (`data-initial-index`) space. In-range phrase
   * runs (and the whitespace between them) get a continuous render-time
   * underline (see `renderPhrasesWithUnderline`) instead of the imperative
   * per-phrase class, which left gaps at the inter-phrase whitespace.
   */
  overlayRanges?: HighlightRange[];
  /**
   * PLAT-10272 (T12) — opt-in: surface the inline "Fix Translation" span-replace
   * affordance over a selected run of translated words. Off by default (epic D9
   * containment); only the Speakers & Transcripts host enables it. When off, this
   * component renders and behaves exactly as before — no selection listener, no
   * pill, no DOM changes.
   */
  enableInlineSpanReplace?: boolean;
  /**
   * PLAT-12047 / PLAT-12056 — the host's hand-off into Translation Review.
   * When set, the extended menu's Fix Translation hands the selection (or
   * the clicked phrase) off instead of editing here, and, when `redub` is
   * on, offers "Re-dub AI Voice" which hands off the same way. Without it
   * the menu edits in place and offers no re-dub. Only meaningful alongside
   * `enableInlineSpanReplace`.
   */
  reviewHandoff?: ReviewHandoffProp;
  /**
   * PLAT-10265 — opt-in: the changed-phrase treatment (green "edited" mark + the
   * hover "Original translation" tooltip with one-click Undo) on each translated
   * word, mirroring the review modal. Off by default (epic D9 containment). When
   * off, this component renders and behaves exactly as before — it renders the
   * plain `Word` atom, adds no `edited` class / `data-*` attributes, and mounts no
   * tooltip.
   */
  enableChangedHighlight?: boolean;
  /**
   * PLAT-10646 — opt-in: right-click opens the standard transcript context
   * menu with these actions after the leading "Fix Translation" entry,
   * instead of the single-action pill. The clicked phrase (widened to a
   * covering selection when one exists) is mapped back to a source element
   * range and dispatched via `onContextMenuAction`. Absent → the legacy
   * pill renders, exactly as before (containment for the other hosts).
   */
  contextMenuActions?: TranscriptContextMenuActions[];
  /**
   * PLAT-11599 — rows of `contextMenuActions` that are listed but inert on
   * this language, each with the tooltip reason. Passed straight through to
   * the menu; the host decides (Remove / Cut Text on any translation).
   */
  contextMenuDisabledReasons?: TranscriptContextMenuDisabledReasons;
  /** Dispatch an extended-menu action with the mapped source range. */
  onContextMenuAction?: (
    action: TranscriptContextMenuActions,
    range: { startIndex: number; endIndex: number },
  ) => void;
}

const logger = LogManager.getLogger('component:TranscriptionTextTranslated');

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs
    .toString()
    .padStart(2, '0')}`;
};

export const TranscriptionTextTranslated: React.FC<TranscriptionTextTranslatedProps> =
  observer(
    ({
      language,
      transcriptElements,
      currentTime,
      isPlaying,
      editable = true,
      onWordClick,
      onSpeakerClick,
      visibleParagraphIds,
      highlightedParagraphIds,
      paragraphHighlightColors,
      activeParagraphId,
      overlayRanges,
      enableInlineSpanReplace = false,
      reviewHandoff,
      enableChangedHighlight = false,
      contextMenuActions,
      contextMenuDisabledReasons,
      onContextMenuAction,
    }) => {
      const { getSpeakerName, getSpeakerColor } = useSpeakerLabels();
      const {
        contextMenu,
        editing,
        editingCurrentValue,
        editingOriginalValue,
        editingIsEdited,
        handleContextMenuRequest,
        handleMenuEdit,
        handleMenuClose,
        handleOpenEditor,
        handleSave,
        handleCancel,
        handleRevert,
      } = usePhraseEditing(language, editable);

      const {
        translation,
        groups,
        filteredElements,
        phrasesByGroup,
        currentLead,
      } = useTranslatedRender(language, transcriptElements, currentTime, {
        includeOutOfScope: false,
      });

      // PLAT-11162 — the reel's structure, second consumer of the same
      // context the original view reads. The stamp runs against THIS view's
      // own filtered array: the groups' element indices are positions into
      // it, so any other array would misplace every heading (the same rule
      // the original view follows with its projection).
      const reelView = useReelTranscriptStructure();
      // Keyed on `stampWords`, not the whole view — folding a Chapter changes
      // the view's identity but not a single word's Section, and this view
      // re-renders on every playback tick.
      const stampWords = reelView?.stampWords;
      const reelWordStamp = useMemo(
        () =>
          stampWords && filteredElements.length > 0
            ? stampWords(filteredElements)
            : undefined,
        [stampWords, filteredElements],
      );
      // The translated view knows the playhead as a phrase lead — an
      // `initial_index` — so section resolution goes lead → array position →
      // stamp.
      const positionByInitialIndex = useMemo(() => {
        const positions = new Map<number, number>();
        filteredElements.forEach((el, position) => {
          positions.set(el.initial_index, position);
        });
        return positions;
      }, [filteredElements]);
      const activeReelSectionId =
        currentLead != null && reelWordStamp
          ? reelWordStamp.sectionIdByWordIndex.get(
              positionByInitialIndex.get(currentLead) ?? -1,
            )
          : undefined;

      // PLAT-11159/11162 — playback reaching a folded passage opens it here
      // too. A lead TRANSITION, not a position: folding around a paused
      // phrase stays folded.
      const prevLeadRef = useRef<number | null>(null);
      useEffect(() => {
        if (currentLead == null || prevLeadRef.current === currentLead) return;
        const isMount = prevLeadRef.current === null;
        prevLeadRef.current = currentLead;
        if (!reelWordStamp) return;
        const position = positionByInitialIndex.get(currentLead);
        if (position === undefined) return;
        revealCollapsedWord(
          reelView,
          filteredElements,
          position,
          reelWordStamp,
          isMount ? 'mount' : 'playback',
        );
      }, [
        currentLead,
        reelView,
        reelWordStamp,
        positionByInitialIndex,
        filteredElements,
      ]);

      // PLAT-10272 — bound to this view's own word container so the inline
      // span-replace layer reads the selection scoped to THIS instance
      // (CLAUDE.md #12), never a sibling transcript's DOM. Declared before the
      // early return so the hook order is stable.
      const wrapperRef = useRef<HTMLDivElement>(null);

      // PLAT-10646 — the span layer's imperative editor opener. The extended
      // menu's Fix Translation entry prefers the span editor (which handles
      // the whole selected run) and falls back to the single-phrase popover
      // when no span is pending (bare right-click, or span path not mounted).
      const spanEditorOpenRef = useRef<(() => boolean) | null>(null);
      // PLAT-10833 — and its re-dub, for the menu's Re-dub AI Voice row: the
      // extended menu hides the span layer's own trigger, which was the only
      // place a re-dub could be asked for on this surface.
      const spanReDubRef = useRef<{
        available: () => boolean;
        run: () => boolean;
        selection: () => PendingSpan | null;
      } | null>(null);
      // The span layer's re-dub, derived from the hand-off: present only
      // where the host offers a re-dub for this language.
      const onRequestReDub = useMemo(
        () =>
          reviewHandoff?.redub
            ? (request: ReDubSelectionRequest) =>
                reviewHandoff.request({ kind: 'redub', ...request })
            : undefined,
        [reviewHandoff],
      );
      const hasExtendedMenu = Boolean(
        contextMenuActions && onContextMenuAction,
      );

      if (!translation) {
        return <Empty>No translation data available for {language}.</Empty>;
      }

      // PLAT-10646 — selection-driven menu, mirroring the original-language
      // view's mouseup behavior (Transcript.tsx `handleMouseUp`): releasing
      // a left-button text selection opens the extended menu at the cursor,
      // no right-click needed. Same 200ms settle so a collapsing selection
      // isn't captured mid-flight.
      const handleWrapperMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!contextMenuActions || !onContextMenuAction) return;
        if (e.button !== 0) return;
        const positionXY = { x: e.clientX, y: e.clientY };
        void (async () => {
          await new Promise(resolve => setTimeout(resolve, 200));
          const activePhrases = translation.phrases.filter(
            p => p.active !== false,
          );
          const resolution = resolveTranslatedSelectionPhrases(
            wrapperRef.current,
            activePhrases,
          );
          if (!resolution.ok) return;
          // Bridge to the live transcript by stable element identity — the
          // covered phrases map to their KEPT source elements' array range.
          // Null when the selection cannot be mapped: the menu still opens
          // (PLAT-11602), with every row but Fix Translation disabled and a
          // hint to pick text that lines up with the original.
          const sourceRange = mapCoveredSourceToElementRange(
            transcriptElements ?? [],
            resolution.phrases,
          );
          // Anchor Fix Translation on the selection's start word span (scoped to
          // this instance's container — CLAUDE.md #12), falling back to the
          // event target. Translated word order ≠ source order, so the start
          // node is the selection's VISUAL start.
          const selection =
            wrapperRef.current?.ownerDocument.getSelection() ?? null;
          const startNode =
            selection && selection.rangeCount > 0
              ? selection.getRangeAt(0).startContainer
              : null;
          const anchorElement =
            (startNode && wrapperRef.current?.contains(startNode)
              ? ((startNode instanceof HTMLElement
                  ? startNode
                  : startNode.parentElement
                )?.closest<HTMLElement>('[data-source-uuid]') ?? null)
              : null) ?? (e.target as HTMLElement);
          handleContextMenuRequest(
            resolution.phrases[0],
            anchorElement,
            positionXY,
            sourceRange,
          );
        })();
      };

      // PLAT-10646 — resolve the right-clicked phrase (widened to the
      // covering selection when one exists) to a source element range for
      // the extended context-menu actions. Computed at menu-request time —
      // the selection can collapse once the menu opens. Null when nothing
      // in the mapped span survives (all removed/cut), in which case the
      // menu keeps its rows but disables all except Fix Translation
      // (PLAT-11602).
      const computeSourceRange = (
        phrase: TranslationPhrase,
      ): { startIndex: number; endIndex: number } | null => {
        if (!transcriptElements || transcriptElements.length === 0) {
          return null;
        }
        // Widen the clicked phrase to the whole covering selection when one
        // exists (membership by reference / stable element id — the same key
        // the guard uses); otherwise act on the single clicked phrase.
        let coveredPhrases: readonly TranslationPhrase[] = [phrase];
        const activePhrases = translation.phrases.filter(
          p => p.active !== false,
        );
        const resolution = resolveTranslatedSelectionPhrases(
          wrapperRef.current,
          activePhrases,
        );
        if (resolution.ok) {
          const coveredIds = new Set(
            resolution.phrases.flatMap(p => p.originalElementIds ?? []),
          );
          const covers =
            resolution.phrases.includes(phrase) ||
            (phrase.originalElementIds?.some(id => coveredIds.has(id)) ??
              false);
          if (covers) coveredPhrases = resolution.phrases;
        }
        return mapCoveredSourceToElementRange(
          transcriptElements,
          coveredPhrases,
        );
      };

      // Recompute isNewSpeaker against the post-filter group sequence so a
      // speaker doesn't appear "headless" when the prior same-speaker paragraph
      // was filtered out (and vice versa: don't duplicate a header when the only
      // intervening group of a different speaker is filtered out).
      let prevVisibleSpeaker: string | undefined;
      const visibleGroups = groups.map((group, gi) => {
        const inScope =
          !visibleParagraphIds ||
          (group.paragraphId !== undefined &&
            visibleParagraphIds.has(group.paragraphId));
        if (!inScope) return null;
        const phrases = phrasesByGroup[gi] ?? [];
        if (phrases.length === 0) return null;
        const isNewSpeaker = group.speakerLabel !== prevVisibleSpeaker;
        prevVisibleSpeaker = group.speakerLabel;
        return { group, gi, phrases, isNewSpeaker };
      });

      // PLAT-11162 — the same rows the original view draws, at the phrase
      // level this view actually renders: groups split where a Section's
      // first phrase begins, headings interleaved as flat siblings, folded
      // passages dropped. Null when no host publishes structure — the map
      // below then renders exactly the pre-reel view.
      const reelRows =
        reelView && reelWordStamp
          ? buildReelTranscriptRows(
              buildTranslatedReelGroups(
                visibleGroups.flatMap(v => (v ? [v.group] : [])),
                visibleGroups.flatMap(v => (v ? [v.phrases] : [])),
                reelWordStamp,
              ),
              reelView.structure,
              reelWordStamp,
              reelView.collapse,
            )
          : null;

      // One paragraph block — speaker header / timestamp row + the phrase
      // run. Shared by the reel-rows path and the pre-reel path so the two
      // can never drift (PLAT-11162).
      const renderGroupBlock = (
        key: string,
        group: {
          paragraphId: string | undefined;
          speakerLabel: string | undefined;
          startTime: number | undefined;
          elements: ReadonlyArray<{
            element: TranscriptElement;
            index: number;
          }>;
        },
        phrases: TranslatedSortedPhrase[],
        isNewSpeaker: boolean,
        followsHeading = false,
      ) => (
        <React.Fragment key={key}>
          {isNewSpeaker && group.speakerLabel && (
            <SpeakerHeader
              contentEditable={false}
              suppressContentEditableWarning
            >
              <SpeakerLabel
                data-testid={`transcript-speaker-label-${group.speakerLabel}`}
                $color={getSpeakerColor(group.speakerLabel)}
                $clickable={!!onSpeakerClick}
                onClick={
                  onSpeakerClick && group.speakerLabel
                    ? e => {
                        e.stopPropagation();
                        // A speaker action covers the whole paragraph in
                        // SOURCE terms. This view's groups are built over a
                        // spine with cut/removed words dropped, and a reel
                        // slice holds only part of its paragraph, so resolve
                        // the source paragraph the clicked group sits in —
                        // the very group the original view would hand over.
                        // The spine keeps element references, so the group's
                        // first element identifies it.
                        const anchor = group.elements[0]?.element;
                        const sectionIndices =
                          groupElementsByParagraph(transcriptElements)
                            .find(g =>
                              g.elements.some(e => e.element === anchor),
                            )
                            ?.elements.map(e => e.index) ?? [];
                        if (sectionIndices.length === 0) {
                          // Only reachable if something upstream stopped
                          // handing this view the source array's own element
                          // objects — the label would go dead, so say why.
                          logger.warn(
                            'Speaker section did not resolve to a source paragraph',
                            {
                              paragraphId: group.paragraphId,
                              speakerLabel: group.speakerLabel,
                              groupElements: group.elements.length,
                              sourceElements: transcriptElements?.length ?? 0,
                            },
                          );
                          return;
                        }
                        onSpeakerClick({
                          speakerLabel: group.speakerLabel as string,
                          sectionIndices,
                          targetElement: e.currentTarget as HTMLElement,
                        });
                      }
                    : undefined
                }
              >
                {getSpeakerName(group.speakerLabel)}
              </SpeakerLabel>
              {group.startTime !== undefined && (
                <SpeakerTime>{formatTime(group.startTime)}</SpeakerTime>
              )}
            </SpeakerHeader>
          )}
          {!isNewSpeaker &&
            !followsHeading &&
            group.startTime !== undefined && (
              <TimestampRow
                contentEditable={false}
                suppressContentEditableWarning
              >
                {formatTime(group.startTime)}
              </TimestampRow>
            )}
          <ParagraphWrapper
            data-paragraph-id={group.paragraphId}
            {...topicRailProps(
              group.paragraphId,
              highlightedParagraphIds,
              activeParagraphId,
              paragraphHighlightColors,
            )}
          >
            {renderPhrasesWithUnderline(phrases, overlayRanges, (p, pi) =>
              renderPhrase(
                p,
                {
                  onWordClick,
                  isCurrent: p.lead === currentLead,
                  isPlaying: !!isPlaying,
                  editable,
                  // With the extended menu configured, resolve the
                  // source range up front so the menu opens with its
                  // dispatchable actions ready (PLAT-10646).
                  onContextMenuRequest: contextMenuActions
                    ? (phrase, anchorElement, positionXY) =>
                        handleContextMenuRequest(
                          phrase,
                          anchorElement,
                          positionXY,
                          computeSourceRange(phrase),
                        )
                    : handleContextMenuRequest,
                  onOpenEditor: handleOpenEditor,
                  enableChangedHighlight,
                  // The changed-phrase mark + click-to-edit are the same
                  // opt-in: only the surfaces that show the green mark
                  // route a click into the editor (whose "Revert to
                  // original" is the Undo). Off → plain seek-on-click.
                  isEditedPhrase:
                    enableChangedHighlight && translation
                      ? isEdited(p.phrase, translation.phrases)
                      : false,
                  // Selection precedence (PLAT-10467) — active only
                  // when the inline span path is mounted on this
                  // surface; reads the selection scoped to this view's
                  // own container.
                  spanSelectionEnabled: enableInlineSpanReplace,
                  selectionContainerRef: wrapperRef,
                  allPhrases: translation?.phrases,
                  hasExtendedMenu,
                },
                pi,
              ),
            )}
          </ParagraphWrapper>
        </React.Fragment>
      );

      return (
        <Wrapper ref={wrapperRef} onMouseUp={handleWrapperMouseUp}>
          {reelRows &&
            reelRows.map((row, rowIndex) => {
              // Same rule as the original view: a heading row directly above
              // already anchors the position, so the bare timestamp under it
              // is suppressed.
              const followsHeading =
                rowIndex > 0 && reelRows[rowIndex - 1].kind !== 'paragraph';
              if (row.kind === 'chapterHeading') {
                const chapterId = row.chapter.chapterId;
                return (
                  <ChapterHeadingRow
                    key={`reel-chapter-${chapterId}`}
                    chapter={row.chapter}
                    speakersByLabel={reelView?.speakersByLabel}
                    collapsed={row.collapsed}
                    onToggle={
                      reelView?.collapse
                        ? () => reelView.collapse?.toggleChapter(chapterId)
                        : undefined
                    }
                  />
                );
              }
              if (row.kind === 'gap') {
                return (
                  <StructureGapRow
                    key={`reel-gap-${row.id}`}
                    id={row.id}
                    endTime={row.endTime}
                  />
                );
              }
              if (row.kind === 'sectionHeading') {
                const sectionId = row.section.sectionId;
                return (
                  <SectionHeadingRow
                    key={`reel-section-${sectionId}`}
                    section={row.section}
                    speakersByLabel={reelView?.speakersByLabel}
                    collapsed={row.collapsed}
                    active={sectionId === activeReelSectionId}
                    onSeek={reelView?.onSectionSeek}
                    onOpenInEditor={reelView?.onSectionOpenInEditor}
                    onRename={reelView?.onSectionRename}
                    onGenerateName={reelView?.onSectionGenerateName}
                    isGeneratingName={reelView?.generatingNameSectionIds?.has(
                      sectionId,
                    )}
                    nameError={reelView?.sectionNameErrors?.[sectionId]}
                    onToggle={
                      reelView?.collapse
                        ? () => reelView.collapse?.toggleSection(sectionId)
                        : undefined
                    }
                  />
                );
              }
              return renderGroupBlock(
                row.group.groupKey,
                row.group,
                row.group.phrases,
                row.group.isNewSpeaker,
                followsHeading,
              );
            })}
          {!reelRows &&
            visibleGroups.map(v => {
              if (!v) return null;
              return renderGroupBlock(
                `group-${v.gi}-${v.group.paragraphId ?? 'noid'}`,
                v.group,
                v.phrases,
                v.isNewSpeaker,
              );
            })}
          {contextMenu &&
            (contextMenuActions && onContextMenuAction ? (
              <TranscriptContextMenu
                positionXY={contextMenu.positionXY}
                // Same rows in the same order whether or not the selection
                // maps (PLAT-11602); an unmappable selection disables every
                // row but Fix Translation instead of collapsing the menu.
                actions={[
                  'fixTranslation',
                  // PLAT-10833 — offered where the host re-dubs; inert, with
                  // the reason, when the selection covers no dub line.
                  ...(onRequestReDub ? (['reDubAIVoice'] as const) : []),
                  ...contextMenuActions,
                ]}
                // PLAT-12056 — the rows that leave for the review end in a
                // chevron here; in place they do not.
                handoffActions={
                  reviewHandoff
                    ? (['fixTranslation', 'reDubAIVoice'] as const)
                    : []
                }
                disabledReasons={{
                  ...(contextMenu.sourceRange
                    ? contextMenuDisabledReasons
                    : disableForUnmappedSelection(
                        contextMenuActions,
                        contextMenuDisabledReasons,
                      )),
                  ...(onRequestReDub && !spanReDubRef.current?.available()
                    ? {
                        reDubAIVoice:
                          'Select words of a dubbed sentence to re-dub them.',
                      }
                    : {}),
                }}
                actionCallback={action => {
                  if (action === 'close') {
                    handleMenuClose();
                    return;
                  }
                  if (action === 'fixTranslation') {
                    // PLAT-12056 — where the host hands off, the fix happens
                    // in Translation Review: the selection's covered words
                    // (or the clicked phrase) go up, and the review opens its
                    // editor over them.
                    if (reviewHandoff) {
                      const span = spanReDubRef.current?.selection() ?? null;
                      const phrase = contextMenu.phrase;
                      const request: ReviewHandoffRequest = span
                        ? {
                            kind: 'fix',
                            coveredIndices: [...span.model.coveredIndices],
                            segmentIds: [
                              ...new Set(
                                span.model.orderedPhrases
                                  .map(p => p.parentId)
                                  .filter((id): id is string => !!id),
                              ),
                            ],
                          }
                        : {
                            kind: 'fix',
                            coveredIndices: [...phrase.originalIndices],
                            segmentIds: phrase.parentId
                              ? [phrase.parentId]
                              : [],
                          };
                      handleMenuClose();
                      reviewHandoff.request(request);
                      return;
                    }
                    // Prefer the span editor when a selected run is pending —
                    // it edits the whole selection; the phrase popover only
                    // edits the clicked phrase.
                    if (spanEditorOpenRef.current?.()) {
                      handleMenuClose();
                      return;
                    }
                    handleMenuEdit();
                    return;
                  }
                  if (action === 'reDubAIVoice') {
                    // PLAT-10833 — the span layer resolved the selection to
                    // its dub lines; hand them to the host, which opens the
                    // Redub Selection popover beside them.
                    handleMenuClose();
                    spanReDubRef.current?.run();
                    return;
                  }
                  const range = contextMenu.sourceRange;
                  handleMenuClose();
                  if (range) onContextMenuAction(action, range);
                }}
              />
            ) : (
              <TranslatedPhraseContextMenu
                positionXY={contextMenu.positionXY}
                onEdit={handleMenuEdit}
                onClose={handleMenuClose}
              />
            ))}
          {editing && (
            <TranslatedPhraseEditorPopover
              anchorElement={editing.anchorElement}
              sourceText={resolveSourceText(
                editing.phrase,
                transcriptElements ?? [],
              )}
              sourceLanguageLabel={
                translation.sourceLanguage
                  ? formatLanguageCaption(translation.sourceLanguage)
                  : undefined
              }
              originalValue={editingOriginalValue}
              currentValue={editingCurrentValue}
              onSave={value => handleSave(editing.phrase, value)}
              onCancel={handleCancel}
              canRevert={editingIsEdited}
              onRevert={() => handleRevert(editing.phrase)}
            />
          )}
          {enableInlineSpanReplace && (
            <InlineSpanReplaceLayer
              containerRef={wrapperRef}
              language={language}
              // The extended menu hosts the Fix Translation entry, so the
              // floating pill would duplicate it (PLAT-10646).
              hideTrigger={hasExtendedMenu}
              openEditorRef={spanEditorOpenRef}
              reDubRef={spanReDubRef}
              onRequestReDub={onRequestReDub}
            />
          )}
        </Wrapper>
      );
    },
  );

interface RenderPhraseOpts {
  onWordClick: ((initialIndex: number) => void) | undefined;
  isCurrent: boolean;
  isPlaying: boolean;
  editable: boolean;
  onContextMenuRequest: (
    phrase: TranslationPhrase,
    anchorElement: HTMLElement,
    positionXY: { x: number; y: number },
  ) => void;
  /** Open the editor popover directly for an edited phrase (double-click-to-fix). */
  onOpenEditor: (phrase: TranslationPhrase, anchorElement: HTMLElement) => void;
  /** PLAT-10265 — render the highlight-capable word atom; off → plain `Word`. */
  enableChangedHighlight: boolean;
  /** Whether this phrase has an applied edit (green mark + click-to-edit). */
  isEditedPhrase: boolean;
  /**
   * PLAT-10467 — true when the inline span path is mounted (== the surface's
   * `enableInlineSpanReplace`). Gates selection precedence: only then does a
   * right-click inside a non-empty selection yield to the span editor. Off (no
   * span path) keeps the right-click menu exactly as before (epic D9
   * containment — "behaves exactly as before when off").
   */
  spanSelectionEnabled: boolean;
  /** This view's own word container, for column-scoped selection resolution
   *  (CLAUDE.md #12 — never `document`). */
  selectionContainerRef: React.RefObject<HTMLElement>;
  /** The surface's phrases; the guard filters to the active set. */
  allPhrases: readonly TranslationPhrase[] | undefined;
  /**
   * PLAT-10646 — true when the extended context menu is configured. Makes a
   * covering-selection right-click open the menu (for its range-targeting
   * actions) instead of being suppressed by the PLAT-10467 guard.
   */
  hasExtendedMenu: boolean;
}

function renderPhrase(
  p: TranslatedSortedPhrase,
  opts: RenderPhraseOpts,
  // Map index — `p.lead` alone isn't unique when data has duplicate-lead phrases.
  keyIndex: number,
): React.ReactNode {
  const value = p.phrase.translatedValue ?? '';
  const isWhitespaceOnly = value.trim() === '';
  // PLAT-10270 — narrow the green to the changed words (mirrors the review
  // modal's `AlignedBlocksView`). A "Fix Translation" correction is one fragment
  // over the whole span, so a per-fragment green paints the whole segment. Break
  // it into word tokens and green only the words that differ from the machine
  // baseline. Switch to token rendering ONLY for a PARTIAL change (some words
  // unchanged); a single word or a full rewrite keeps the whole-word mark.
  const wordTokens =
    opts.isEditedPhrase && opts.enableChangedHighlight
      ? resolveChangedWordTokens(p.phrase, opts.allPhrases)
      : null;
  const partialChange =
    !!wordTokens &&
    wordTokens.some(t => t.changed) &&
    wordTokens.some(t => !t.changed && t.text.trim().length > 0);
  const classes: string[] = [];
  if (isWhitespaceOnly) classes.push('whitespace');
  if (opts.isCurrent) {
    classes.push('current');
    if (!opts.isPlaying) classes.push('cursor-before');
  }
  // Changed-phrase mark (PLAT-10265). Only present when the opt-in is on AND the
  // phrase resolved as edited — off path leaves `classes` exactly as before. For
  // a PARTIAL change the whole-word mark is dropped in favour of the per-word
  // marks rendered below (PLAT-10270).
  if (opts.isEditedPhrase && !partialChange) classes.push('edited');
  const handleContextMenu = (e: React.MouseEvent<HTMLSpanElement>) => {
    if (!opts.editable || isWhitespaceOnly) return;
    // Selection precedence (PLAT-10467): when the span path is mounted and a
    // non-empty selection already covers this word, the span editor owns the
    // TRANSLATION edit. Without the extended menu that meant suppressing the
    // single-word menu entirely (preventDefault + return, no menu). With the
    // extended menu configured (PLAT-10646) the right-click still opens it —
    // its actions (save as clip, pull quote, …) target the selected range,
    // which the span pill can't do — while the pill stays available above
    // the selection for the translation fix itself.
    if (
      !opts.hasExtendedMenu &&
      opts.spanSelectionEnabled &&
      isPhraseInTranslatedSelection(
        opts.selectionContainerRef.current,
        opts.allPhrases,
        p.phrase,
      )
    ) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    opts.onContextMenuRequest(p.phrase, e.currentTarget, {
      x: e.clientX,
      y: e.clientY,
    });
  };
  // Off path renders the plain shared `Word`; on path renders the renderer-local
  // `styled(Word)` wrapper that adds ONLY the green highlight rule (Word itself is
  // never modified).
  const WordComponent = opts.enableChangedHighlight ? HighlightableWord : Word;
  // Click routing: every word seeks on click, edited ones included — the green
  // mark must not hijack playback navigation. An edited phrase opens the editor
  // (whose "Revert to original" is the Undo) on DOUBLE-click; right-click →
  // Fix Translation also reaches it. Whitespace tokens stay inert.
  const { onWordClick } = opts;
  const handleClick =
    isWhitespaceOnly || !onWordClick ? undefined : () => onWordClick(p.lead);
  const handleDoubleClick =
    !isWhitespaceOnly && opts.isEditedPhrase && opts.editable
      ? (e: React.MouseEvent<HTMLSpanElement>) => {
          // A double-click also selects the word; clear it so the
          // selection-driven menu (handleWrapperMouseUp) doesn't open on top
          // of the editor.
          e.currentTarget.ownerDocument.getSelection()?.removeAllRanges();
          opts.onOpenEditor(p.phrase, e.currentTarget);
        }
      : undefined;
  return (
    <WordComponent
      key={`p-${p.lead}-${keyIndex}`}
      data-initial-index={p.lead}
      // PLAT-10611 (F5) — stamp the lead element's stable UUID beside the
      // retained integer lead. Same value the cross-language resolver returns for
      // this phrase (both read `TranslatedSortedPhrase.leadUuid`), so the link
      // resolves this span by identity FIRST; omitted when the lead has no id.
      data-source-uuid={p.leadUuid}
      // PLAT-12024 — the dub line this phrase belongs to (the engine's segment
      // id), so a landed re-dub can light up exactly the words it re-recorded.
      data-segment-id={p.phrase.parentId}
      className={classes.length > 0 ? classes.join(' ') : undefined}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
    >
      {partialChange && wordTokens
        ? wordTokens.map((t, ti) =>
            t.changed ? (
              <ChangedWordMark key={`w-${ti}`}>{t.text}</ChangedWordMark>
            ) : (
              <React.Fragment key={`w-${ti}`}>{t.text}</React.Fragment>
            ),
          )
        : value}
    </WordComponent>
  );
}

/* Cursor allowance only — the topic-rail gutter lives on the scroll container
   so the text lines up with the toolbar (PLAT-11039). */
const Wrapper = styled.div`
  padding-left: ${CURSOR_ALLOWANCE_PX}px;
`;

const ParagraphWrapper = styled.div`
  display: block;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }

  ${topicRailCss}
`;

const TimestampRow = styled.div`
  text-align: right;
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular);
  line-height: 1;
  margin-top: -11px;
  margin-bottom: 5px;
  user-select: none;
  pointer-events: none;
`;

// PLAT-12024 — the "just changed" wash: the saved-clip card's green, held then
// faded over the same six seconds, on the words a re-dub just re-recorded.
const redubLandedFade = keyframes`
  0% {
    background-color: color-mix(in srgb, var(--primary) 28%, transparent);
  }
  70% {
    background-color: color-mix(in srgb, var(--primary) 28%, transparent);
  }
  100% {
    background-color: transparent;
  }
`;

const cursorAnimation = keyframes`
  100% {
    opacity: 0;
  }
`;

// Exported (PLAT-10264) so the Translation Review aligned-blocks view reuses
// the exact translated word atom — `data-initial-index` stamping + the
// `.current` / `.current.cursor-before` playhead classes — instead of forking
// a parallel translated renderer (epic containment rule D9). `TranscriptionText
// Translated` keeps owning the prose layout; only this atom is shared.
export const Word = styled.span`
  color: var(--text-secondary);
  position: relative;
  cursor: pointer;

  /* PLAT-10066 — overlay underline + azure hover on translated phrase
     spans. Shared with the original-language path; visual behavior is
     identical, just keyed on data-initial-index instead of data-index. */
  ${overlayHighlightStyles}

  &.whitespace {
    white-space: pre-wrap;
    cursor: default;
  }

  /* PLAT-11765 — the translated twin of WordLight's read class: a phrase the
     playhead has passed reads in the primary text token. Declared BEFORE the
     current rule so the gold playhead still wins on the phrase being spoken. */
  &.read {
    color: var(--text-primary);
  }
  /* The twin of read: text the playhead has not reached yet. Dimmer than the
     atom's own default so the two states separate at a glance — at the default
     secondary color the gap was about 2x in luminance, which read as one color
     in a long transcript; muted makes it about 5x. */
  &.unread {
    color: var(--text-muted);
  }
  /* PLAT-11765 — the translated twin of WordLight's playing class: the segment
     being played reads slightly bolder, drawn with a hairline shadow so the
     text keeps its metrics and nothing re-wraps as playback moves. */
  &.playing {
    text-shadow: 0 0 0.35px currentColor;
  }

  /* PLAT-12024 — a re-dub just landed on this word. Declared before .current
     so the playhead's gold still wins on the phrase being spoken. */
  &.redub-landed {
    border-radius: 3px;
    animation: ${redubLandedFade} 6000ms ease-out forwards;
  }

  &.current {
    color: black;
    background-color: #dfb615;
  }

  &.current.cursor-before {
    color: var(--text-secondary);
    background-color: transparent;
  }
  &.current.cursor-before::before {
    content: '';
    display: inline-block;
    width: 2px;
    height: 1em;
    background-color: #dfb615;
    vertical-align: text-bottom;
    border-radius: 1px;
    animation: ${cursorAnimation} steps(2) 1.5s infinite;
    will-change: opacity;
    margin-right: 1px;
  }

  /* Hover declared last + !important so it wins over .current /
     .current.cursor-before for the playhead phrase (which is .current
     by default at t=0). Mirrors WordLight in TranscriptionText.tsx. */
  &:hover {
    color: lightgreen !important;
  }
`;

// PLAT-10265 — the changed-phrase highlight word atom for the prose renderer.
// Extends the shared `Word` with ONLY the kit's green "edited" rule (epic
// containment rule D9: no review styling leaks into the shared atom). Mirrors the
// review modal's `TranslatedWord`. Used only on the `enableChangedHighlight` path;
// `Word` itself is untouched.
// PLAT-10270 — the per-word green mark used when only part of a "Fix
// Translation" correction changed. Same green tint as the whole-word edited
// background, applied to individual changed words so the unchanged remainder of
// the segment stays unmarked. Mirrors the review modal's `ChangedWordMark`.
const ChangedWordMark = styled.span`
  background-color: ${CHANGED_PHRASE_TINT};
  border-radius: 3px;
`;

const HighlightableWord = styled(Word)`
  ${changedPhraseHighlightCss}

  /* Under the PLAYING playhead the gold fill owns the whole-word background;
     suppress the inner per-word green there to match the whole-word edited
     rule (the green returns the instant playback moves on). */
  &.current:not(.cursor-before) ${ChangedWordMark} {
    background-color: transparent;
  }
`;

const Empty = styled.div`
  color: var(--text-muted);
  font-size: var(--text-xs);
  padding: 20px;
  text-align: center;
`;
